import cn from 'classnames'
import {
  IFormJBPHPContactChangeRequest,
  IFormJBPHPContactRoles,
} from 'legacy/lut/classes/models/partner360/IFormHPContacts'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { CommitmentTypes } from 'legacy/lut/components/widgets/public/partner360/commitment/models'
import { selectCommitmentItemsByProgramType } from 'legacy/lut/features/partner360/commitment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { Nullable } from 'clickNgo/models/utils'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { selectPublishingMode } from 'core/store/selectors/selectPublishingMode'

import { TRANSLATION_KEYS } from '../../translations'

import { AcknowledgementToggle } from './components/acknowledgementToggle/AcknowledgementToggle'
import { CreateHpContact } from './components/createHpContact/CreateHpContact'
import { HPContactInfo } from './components/hpContactInfo/HPContactInfo'
import { HpContactsActions } from './components/hpContactsActions/HpContactsActions'
import { RoleName } from './components/roleName/RoleName'
import { hasRole } from './helpers/hasRole'
import { AssignmentCommitmentStatus } from './models'

import styles from './RolesAssignment.module.scss'

interface IRolesAssignmentProps {
  contacts: IFormJBPHPContactChangeRequest[]
  roles: IFormJBPHPContactRoles[]
  allRoles: IFormJBPHPContactRoles[]
  crudKey: string
  partnerId: string
  leadPbmId: number
  fetchPartnerRoles: (value: boolean) => Promise<IFormJBPHPContactRoles[]>
  programType: ProgramTypes
  fYear: number
  onUpdateWizard: () => void
  setIsLoading: (flag: boolean) => void
}

interface IRoleTableRow {
  role: IFormJBPHPContactRoles
  contact: IFormJBPHPContactChangeRequest
  isFirstRowOfRole: boolean
  rowSpan: number
  commitmentStatus: AssignmentCommitmentStatus
  commitmentItemEntryId: number | null
  roleGroupIndex: number
}

const COLLAPSE_MS = 260

export const RolesAssignment = ({
  contacts,
  roles,
  crudKey,
  allRoles,
  partnerId,
  leadPbmId,
  fetchPartnerRoles,
  programType,
  fYear,
  onUpdateWizard,
  setIsLoading,
}: IRolesAssignmentProps) => {
  const commitmentItems = useSelector(selectCommitmentItemsByProgramType)
  const isEditMode = useSelector(selectPublishingMode)

  const [deletingId, setDeletingId] = useState<Nullable<number>>(null)
  const [shouldRemoveFromRender, setShouldRemoveFromRender] = useState(false)
  const [isHiddenUntilConfirmed, setIsHiddenUntilConfirmed] = useState(false)

  const collapseTimerRef = useRef<number | null>(null)

  useEffect(() => {
    if (deletingId == null || !isHiddenUntilConfirmed) return

    const stillExists = contacts.some((c) => c.EntryId === deletingId)
    if (stillExists) return

    setDeletingId(null)
    setIsHiddenUntilConfirmed(false)
    setShouldRemoveFromRender(false)
  }, [contacts, deletingId, isHiddenUntilConfirmed])

  useEffect(() => {
    return () => {
      if (collapseTimerRef.current != null) {
        window.clearTimeout(collapseTimerRef.current)
        collapseTimerRef.current = null
      }
    }
  }, [])

  const handleSetDeleting = useCallback((entryId: Nullable<number>) => {
    if (entryId == null) return

    setDeletingId(entryId)
    setIsHiddenUntilConfirmed(true)
    setShouldRemoveFromRender(false)

    if (collapseTimerRef.current != null) {
      window.clearTimeout(collapseTimerRef.current)
    }

    collapseTimerRef.current = window.setTimeout(() => {
      setShouldRemoveFromRender(true)
    }, COLLAPSE_MS)
  }, [])

  const contactsForRender = useMemo(() => {
    const shouldFilter = isHiddenUntilConfirmed && shouldRemoveFromRender && deletingId != null
    if (!shouldFilter) {
      return contacts
    }

    return contacts.filter((c) => c.EntryId !== deletingId)
  }, [contacts, deletingId, isHiddenUntilConfirmed, shouldRemoveFromRender])

  const tableRows = useMemo<IRoleTableRow[]>(() => {
    return roles.reduce<IRoleTableRow[]>((acc, role, roleIndex) => {
      const relatedContacts = contactsForRender.filter((c) => hasRole(c, role.Roles))
      if (relatedContacts.length === 0) return acc

      const rowsForRole = relatedContacts.map((contact, index) => {
        const hpCommitment = commitmentItems.find(
          (item) =>
            item.CommitmentType === CommitmentTypes.PBM && item.SignerEmail === contact.Email
        )

        const commitmentStatus = hpCommitment
          ? hpCommitment.IsOptional
            ? AssignmentCommitmentStatus.Optional
            : AssignmentCommitmentStatus.Mandatory
          : AssignmentCommitmentStatus.None

        return {
          role,
          contact,
          isFirstRowOfRole: index === 0,
          rowSpan: index === 0 ? relatedContacts.length : 0,
          commitmentStatus,
          commitmentItemEntryId: hpCommitment?.EntryId || null,
          roleGroupIndex: roleIndex,
        }
      })

      return acc.concat(rowsForRole)
    }, [])
  }, [roles, contactsForRender, commitmentItems])

  return (
    <div>
      <CreateHpContact
        partnerId={partnerId}
        allRoles={allRoles}
        leadPbmId={leadPbmId}
        crudKey={crudKey}
        fetchPartnerRoles={fetchPartnerRoles}
        onUpdateWizard={onUpdateWizard}
      />

      <table className={styles.table}>
        <colgroup>
          <col className={styles.colRole} />
          <col className={styles.colContact} />
          <col className={styles.colBU} />
          <col className={styles.colAcknowledge} />
          {isEditMode && <col className={styles.colActions} />}
        </colgroup>

        <thead>
          <tr>
            <th>
              <Translation text={TRANSLATION_KEYS.columns.role} />
            </th>
            <th>
              <Translation text={TRANSLATION_KEYS.columns.contact} />
            </th>
            <th>
              <Translation text={TRANSLATION_KEYS.columns.bu} />
            </th>
            <th>
              <Translation text={TRANSLATION_KEYS.columns.includeInAcknowledge} />
            </th>
            <RenderWhen condition={isEditMode}>
              <th />
            </RenderWhen>
          </tr>
        </thead>

        <tbody>
          {tableRows.map((row) => {
            const entryId = row.contact?.EntryId ?? null
            const key = `${row.role.UniqueId}-${entryId ?? 'empty'}`
            const isRowDeleting = entryId != null && deletingId === entryId

            return (
              <tr
                key={key}
                className={cn(
                  {
                    [styles.roleRowEven]: row.roleGroupIndex % 2 === 0,
                    [styles.roleRowOdd]: row.roleGroupIndex % 2 === 1,
                  },
                  { [styles.deletingRow]: isRowDeleting }
                )}
              >
                {row.isFirstRowOfRole && (
                  <td rowSpan={row.rowSpan} className={styles.roleCell}>
                    <div className={styles.cellInner}>
                      <RoleName
                        role={row.role.Roles}
                        allRoles={allRoles}
                        partnerId={partnerId}
                        crudKey={crudKey}
                        leadPbmId={leadPbmId}
                        fetchPartnerRoles={fetchPartnerRoles}
                        onUpdateWizard={onUpdateWizard}
                      />
                    </div>
                  </td>
                )}

                <td className={styles.contactCell}>
                  <div className={styles.cellInner}>
                    <HPContactInfo contact={row.contact} />
                  </div>
                </td>

                <td>
                  <div className={styles.cellInner}>{row.contact?.BusinessUnit ?? '—'}</div>
                </td>

                <td>
                  <div className={styles.cellInner}>
                    <AcknowledgementToggle
                      contact={row.contact}
                      commitmentStatus={row.commitmentStatus}
                      programType={programType}
                      fYear={fYear}
                      partnerId={partnerId}
                      role={row.role.Roles}
                      commitmentItemEntryId={row.commitmentItemEntryId}
                    />
                  </div>
                </td>

                <RenderWhen condition={isEditMode}>
                  <td className={styles.actionsCell}>
                    <div className={styles.cellInner}>
                      <HpContactsActions
                        contact={row.contact}
                        partnerId={partnerId}
                        crudKey={crudKey}
                        leadPbmId={leadPbmId}
                        allRoles={allRoles}
                        fetchPartnerRoles={fetchPartnerRoles}
                        onUpdateWizard={onUpdateWizard}
                        setIsLoading={setIsLoading}
                        setIsDeleting={handleSetDeleting}
                      />
                    </div>
                  </td>
                </RenderWhen>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
